import { Tooltip, TooltipPosition, TooltipPositionType } from 'components/tooltip';
import { useMemo, useState } from 'react';
import { calculateArraySingleHeaderWidth, hasValue, isNotEmpty } from 'utility/functions';

import classNames from 'classnames';
import { DynamicImage } from 'components/Image';
import { useInfoTipo, useIsAvvenimento, useIsLivePage, useIsMarcatoriPage } from 'features/sport/hooks';
import { useTranslation } from 'hooks/useLingUI';
import useWindowSize from 'hooks/useWindowSize';
import { ItemWithTranslation } from 'types/swagger';
import { isHappybetSite } from 'utility/constant';
import styles from './ScommesseTableTitle.module.scss';
import { SportTableScommesseInfoAggSelector } from 'features/sport/components/sportTableScommesse/sportTableScommesseInfoAggSelector';

export type ScommesseTableTitleProps = ItemWithTranslation<{
  labelSx?: string;
  preLabelSx?: string;
  labelDx?: string;
  tooltipText?: string;
}> & {
  className?: string;
  tooltipPosition?: TooltipPositionType;
  headerWidth?: string;
  isEstesa?: boolean;
  iconSrc?: string;
  setIsTooltipClicked?: React.Dispatch<React.SetStateAction<boolean>>;
  isTooltipClicked?: boolean;
  infoTipoScommessaKey?: string;
};

export const ScommesseTableTitle = ({
  labelSx,
  labelSxTrKey,
  preLabelSx,
  preLabelSxTrKey,
  labelDx,
  labelDxTrKey,
  className,
  tooltipPosition,
  headerWidth,
  isEstesa,
  iconSrc,
  isTooltipClicked,
  setIsTooltipClicked,
  infoTipoScommessaKey,
}: ScommesseTableTitleProps) => {
  const infoTipoScommessa = useInfoTipo(infoTipoScommessaKey);
  const {
    toolTip,
    toolTipTrKey,
    tipoVisualizzazione,
    sizeInfoAggiuntive,
    numeroEsiti,
    headers,
    headersInfoAggiuntive,
  } = infoTipoScommessa ?? {};
  const { t } = useTranslation();
  const hasInfoAgg = (headersInfoAggiuntive ?? []).length > 0;

  const { isMobile } = useWindowSize();
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const isLive = useIsLivePage();
  const isAvvenimento = useIsAvvenimento();
  const isMarcatore = useIsMarcatoriPage();

  const dynamicHeaderWidth = headerWidth && !isMobile && !isAvvenimento ? (isEstesa ? '100%' : headerWidth) : 'auto';

  const headerBoxesWidth = calculateArraySingleHeaderWidth(
    tipoVisualizzazione,
    sizeInfoAggiuntive,
    numeroEsiti,
    (headers ?? []).filter((elemento) => elemento !== 'ALTRO'), // TODO capire come fare ad escludere il controllo sui risultati non visibili nell'elenco
    isMarcatore
  );
  let { sxText, dxText } = useMemo(() => {
    const dxText = t(labelDxTrKey, labelDx);
    let sxText: string | undefined;
    if (isHappybetSite && preLabelSx && (labelSx || labelSxTrKey)) {
      sxText = t(preLabelSxTrKey, preLabelSx) + ' - ' + t(labelSxTrKey, labelSx);
    } else if (labelSx || labelSxTrKey) {
      sxText = t(labelSxTrKey, labelSx);
    }

    return { sxText, dxText };
  }, [labelDx, labelDxTrKey, labelSx, labelSxTrKey, preLabelSx, preLabelSxTrKey, t]);

  const calculatedHeaderWidth = useMemo(() => {
    let headerWith: number = +headerBoxesWidth.reduce((x, y) => +x + +y);
    if (toolTip) {
      headerWith -= 24;
    }
    return headerWith;
  }, [headerBoxesWidth, toolTip]);

  return (
    <div
      className={classNames(styles.container, className, {
        [styles.withTooltip]: hasValue(toolTip),
        [styles.withTwoLabel]: labelSx,
        [styles.tooltipVisible]: showTooltip,
        [styles.isAvvenimento]: isAvvenimento,
        [styles.isAvvenimentoLive]: isAvvenimento && isLive,
        [styles.withIcon]: !!iconSrc,
      })}
      style={{ width: headerWidth && dynamicHeaderWidth }}
      suppressHydrationWarning
    >
      {isNotEmpty(iconSrc) && (
        <DynamicImage
          src={`${iconSrc}`}
          width={16}
          height={16}
          alt="Scommesse"
          layout={'fixed'}
          loading="lazy"
          decoding="async"
          className={styles.icon}
        />
      )}
      {sxText && (
        <span className={styles.labelSx} suppressHydrationWarning>
          {sxText}
        </span>
      )}
      <p className={styles.labelWrapper} suppressHydrationWarning>
        <span
          className={classNames(styles.labelDx)}
          style={{ width: hasInfoAgg && !isAvvenimento ? calculatedHeaderWidth : 'unset' }}
          suppressHydrationWarning
        >
          {hasInfoAgg && !isAvvenimento && !isEstesa
            ? headersInfoAggiuntive?.map((header, index) => {
                return (
                  <SportTableScommesseInfoAggSelector
                    header={header}
                    infoTipoScommessaKey={infoTipoScommessaKey ?? ''}
                    key={`index_${infoTipoScommessaKey}`}
                    width={headerBoxesWidth[index]}
                  />
                );
              })
            : dxText}
        </span>
        {toolTip && (
          <Tooltip
            content={t(toolTipTrKey, toolTip)}
            iconColor={'--color-neutral-01'}
            position={tooltipPosition ? tooltipPosition : TooltipPosition.BOTTOM_LEFT}
            setStateOnClick={setShowTooltip}
            stateOnClick={showTooltip}
            setIsClicked={setIsTooltipClicked}
            isClicked={isTooltipClicked}
          />
        )}
      </p>
    </div>
  );
};
